import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from '@/providers/AuthProvider';

const LanguageSelector: React.FC<{ iconOnly?: boolean }> = ({ iconOnly = false }) => {
  const { i18n } = useTranslation();
  const { user } = useAuth();

  useEffect(() => {
    // Load user language preference from Supabase when component mounts
    const loadLanguagePreference = async () => {
      if (!user) return;
      
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('language')
          .eq('id', user.id)
          .single();
        
        if (error) {
          console.error("Error loading language settings:", error);
          return;
        }
        
        // Check if settings and language preference exists
        if (data?.language) {
          // Only change language if different from current to avoid unnecessary reloads
          if (i18n.language !== data?.language) {
            i18n.changeLanguage(data?.language);
          }
        }
      } catch (err) {
        console.error("Failed to load language preference:", err);
      }
    };
    
    loadLanguagePreference();
  }, [user, i18n]);

  const changeLanguage = async (lng: string) => {
    // Change the language in i18n
    i18n.changeLanguage(lng);
    
    // Save the preference to Supabase if user is authenticated
    if (user) {
      try {
              
        // Update the profile with new settings
        const { error } = await supabase
          .from('profiles')
          .update({ language: lng })
          .eq('id', user.id);
        
        if (error) {
          console.error("Error saving language preference:", error);
        }
      } catch (err) {
        console.error("Failed to save language preference:", err);
      }
    }
  };

  return (
    <div className="flex items-center space-x-2">
      <Select 
        value={i18n.language} 
        onValueChange={changeLanguage}
        onOpenChange={(open) => {
          // Prevent propagation when opening/closing the select
          if (open) {
            setTimeout(() => {
              const content = document.querySelector('.select-content');
              if (content) {
                content.classList.add('select-content');
              }
            }, 0);
          }
        }}
      >
        <SelectTrigger hideChevron={iconOnly} className={iconOnly ? "h-9 w-9 p-0 flex items-center justify-center bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-digger-blue hover:bg-digger-blue-light/10 focus:ring-2 focus:ring-digger-blue focus:border-digger-blue transition-colors duration-150 rounded-sm" : "h-9 px-4 text-sm font-medium bg-white dark:bg-gray-800 border border-digger-blue-light dark:border-gray-700 text-digger-blue shadow-sm hover:bg-digger-blue-light/10 focus:ring-2 focus:ring-digger-blue focus:border-digger-blue transition-colors duration-150 rounded-sm flex items-center"}>
          <Globe className="h-5 w-5 text-digger-blue mx-auto" />
          {!iconOnly && <SelectValue placeholder="Select language" className="text-digger-blue" />}
          {/* Only render chevron manually if iconOnly is false and hideChevron is true (should never happen), otherwise let SelectTrigger render it */}
        </SelectTrigger>
        <SelectContent className="select-content bg-white dark:bg-gray-800 text-digger-blue z-50 border border-digger-blue-light dark:border-gray-700 shadow-lg">
          <SelectItem value="en">English</SelectItem>
          <SelectItem value="fr">Français</SelectItem>
          <SelectItem value="nl">Nederlands</SelectItem>
          <SelectItem value="es">Español</SelectItem>
          <SelectItem value="de">Deutsch</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
};

export default LanguageSelector;